import { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import GroupIcon from '@mui/icons-material/Group';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { useAuth } from '../../contexts/AuthContext';
import { useUsers } from '../../api/admin';
import { requestFieldErrors, requestMessage } from '../../utils/requestError';
import {
  TenantRow,
  switchTenant,
  useCreateTenant,
  useGrantTenantAccess,
  useRevokeTenantAccess,
  useTenantAccess,
  useTenants,
  useUpdateTenant,
} from '../../api/tenants';
import AdminPageHeader from './AdminPageHeader';

/**
 * Admin → Client organisations: create organisations (tenants), decide who from your
 * own organisation can work in each, and switch into them.
 *
 * Visible to FiSci, and to people whose organisation FiSci has allowed to
 * create organisations *and* who hold the "Create organisations" capability.
 * A creator sees the organisations theirs created; FiSci sees all of them.
 */


export default function TenantsManagement() {
  const { user } = useAuth();
  const isSuperuser = !!user?.is_superuser;
  const { data: tenants = [], isLoading } = useTenants();
  const [filter, setFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [renaming, setRenaming] = useState<TenantRow | null>(null);
  const [accessFor, setAccessFor] = useState<TenantRow | null>(null);
  const [switching, setSwitching] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.slug ?? '').includes(q) ||
        (t.parent_name ?? '').toLowerCase().includes(q)
    );
  }, [tenants, filter]);

  const enter = async (tenant: TenantRow) => {
    setSwitching(tenant.id);
    try {
      await switchTenant(tenant.id);
    } catch (err: unknown) {
      setSwitching(null);
      setSnackbar(requestMessage(err, `Could not switch to ${tenant.name}`));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <AdminPageHeader
        title="Client organisations"
        description="Organisations you run Mitigate for. Each is separate: its own people, sign-in and data. Enter one to work in it."
        audience="partners"
        action={
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Create organisation
          </Button>
        }
      />

      {isSuperuser && tenants.length > 8 && (
        <TextField
          size="small"
          placeholder="Search organisations"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ mb: 2, minWidth: 320 }}
        />
      )}

      {visible.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            No organisations yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create one for each client you work for. You can give yourself and
            your colleagues access as you create it.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                {isSuperuser && <TableCell sx={{ fontWeight: 'bold' }}>Created by</TableCell>}
                <TableCell sx={{ fontWeight: 'bold' }}>Users</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Single sign-on</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((tenant) => (
                <TableRow key={tenant.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tenant.name}
                      {tenant.is_home && <Chip size="small" label="Yours" />}
                      {!tenant.is_home && tenant.has_access && (
                        <Chip size="small" color="success" variant="outlined" label="You have access" />
                      )}
                      {isSuperuser && tenant.allow_child_tenants && (
                        <Chip size="small" variant="outlined" label="Creates organisations" />
                      )}
                    </Box>
                    {tenant.slug && (
                      <Typography variant="caption" color="text.secondary">
                        /login/{tenant.slug}
                      </Typography>
                    )}
                  </TableCell>
                  {isSuperuser && <TableCell>{tenant.parent_name ?? 'FiSci'}</TableCell>}
                  <TableCell>{tenant.user_count}</TableCell>
                  <TableCell>{tenant.sso_configured ? 'Configured' : '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {!tenant.is_home && (tenant.has_access || isSuperuser) && (
                      <Tooltip title={`Work in ${tenant.name}`}>
                        <span>
                          <IconButton onClick={() => enter(tenant)} disabled={switching !== null}>
                            {switching === tenant.id ? <CircularProgress size={18} /> : <LoginIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!tenant.is_home && (
                      <Tooltip title="Who can work in it">
                        <IconButton onClick={() => setAccessFor(tenant)}>
                          <GroupIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(!tenant.is_home || isSuperuser) && (
                      <Tooltip title="Rename">
                        <IconButton onClick={() => setRenaming(tenant)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {createOpen && (
        <CreateTenantDialog
          isSuperuser={isSuperuser}
          tenants={tenants}
          onClose={() => setCreateOpen(false)}
          onCreated={(row) => {
            setCreateOpen(false);
            setSnackbar(`${row.name} created${row.has_access ? ' — you can enter it from the list' : ''}`);
          }}
        />
      )}
      {renaming && (
        <RenameTenantDialog
          tenant={renaming}
          isSuperuser={isSuperuser}
          onClose={() => setRenaming(null)}
          onSaved={() => {
            setRenaming(null);
            setSnackbar('Organisation updated');
          }}
        />
      )}
      {accessFor && <AccessDialog tenant={accessFor} onClose={() => setAccessFor(null)} />}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function CreateTenantDialog({
  isSuperuser,
  tenants,
  onClose,
  onCreated,
}: {
  isSuperuser: boolean;
  tenants: TenantRow[];
  onClose: () => void;
  onCreated: (row: TenantRow) => void;
}) {
  const create = useCreateTenant();
  const [name, setName] = useState('');
  const [grantMe, setGrantMe] = useState(true);
  const [parent, setParent] = useState<TenantRow | null>(null);
  const [withAdmin, setWithAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = () => {
    setErrors({});
    create.mutate(
      {
        name: name.trim(),
        grant_me_access: grantMe,
        parent_id: isSuperuser ? parent?.id ?? null : undefined,
        first_admin: withAdmin
          ? { email: adminEmail.trim(), password: adminPassword || undefined }
          : null,
      },
      {
        onSuccess: onCreated,
        onError: (err: unknown) => {
          const fields = requestFieldErrors(err);
          setErrors(Object.keys(fields).length ? fields : { detail: requestMessage(err, 'Could not create it') });
        },
      }
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create organisation</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!errors.name}
          helperText={errors.name || ' '}
        />
        {isSuperuser && (
          <Autocomplete
            options={tenants}
            getOptionLabel={(t) => t.name}
            value={parent}
            onChange={(_e, value) => setParent(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Belongs to (optional)"
                helperText="Leave empty for a direct FiSci customer. Pick a partner to let them manage it."
              />
            )}
          />
        )}
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Checkbox checked={grantMe} onChange={(e) => setGrantMe(e.target.checked)} />}
          label="Give me access to it"
        />
        <FormControlLabel
          control={<Checkbox checked={withAdmin} onChange={(e) => setWithAdmin(e.target.checked)} />}
          label="Add its first administrator"
        />
        {withAdmin && (
          <Box sx={{ ml: 4 }}>
            <TextField
              fullWidth
              margin="dense"
              size="small"
              label="Administrator's email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
            />
            <TextField
              fullWidth
              margin="dense"
              size="small"
              type="password"
              autoComplete="new-password"
              label="Password (leave blank if they'll use single sign-on)"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
            />
          </Box>
        )}
        {(errors.first_admin || errors.slug || errors.detail || errors.parent_id) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.first_admin || errors.slug || errors.parent_id || errors.detail}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!name.trim() || create.isPending || (withAdmin && !adminEmail.trim())}
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RenameTenantDialog({
  tenant,
  isSuperuser,
  onClose,
  onSaved,
}: {
  tenant: TenantRow;
  isSuperuser: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const update = useUpdateTenant();
  const [name, setName] = useState(tenant.name);
  const [slug, setSlug] = useState(tenant.slug ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = () => {
    setErrors({});
    const body: { id: number; name?: string; slug?: string } = { id: tenant.id };
    if (name.trim() !== tenant.name) body.name = name.trim();
    if (isSuperuser && slug !== (tenant.slug ?? '')) body.slug = slug;
    update.mutate(body, {
      onSuccess: onSaved,
      onError: (err: unknown) => {
        const fields = requestFieldErrors(err);
        setErrors(Object.keys(fields).length ? fields : { name: requestMessage(err, 'Could not save') });
      },
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rename organisation</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!errors.name}
          helperText={errors.name || ' '}
        />
        {isSuperuser && (
          <TextField
            fullWidth
            margin="dense"
            label="Short name (sign-in link)"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            error={!!errors.slug}
            helperText={errors.slug || `Sign-in link: /login/${slug || '…'}`}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={update.isPending || !name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AccessDialog({ tenant, onClose }: { tenant: TenantRow; onClose: () => void }) {
  const { user } = useAuth();
  // Colleagues come from the Users list of the organisation this session is
  // in; while working in another organisation that list is the wrong one.
  const switched = !!user?.is_switched;
  const { data: access = [], isLoading } = useTenantAccess(tenant.id);
  const { data: users = [] } = useUsers();
  const grant = useGrantTenantAccess(tenant.id);
  const revoke = useRevokeTenantAccess(tenant.id);
  const [error, setError] = useState<string | null>(null);

  const linkedIds = new Set(access.map((a) => a.home_user.id));
  // Colleagues = people in my own organisation; linked accounts never appear
  // in the Users list of another tenant, but filter them out defensively.
  const candidates = users.filter(
    (u) => !linkedIds.has(u.id) && u.is_active !== false && u.auth_source !== 'linked'
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Who can work in {tenant.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          People listed here can enter {tenant.name} from their account menu,
          as one of its administrators. Removing someone takes effect within a
          minute.
        </Typography>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : access.length === 0 ? (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Nobody yet.
          </Typography>
        ) : (
          <List dense sx={{ mb: 2 }}>
            {access.map((a) => (
              <ListItem
                key={a.id}
                secondaryAction={
                  <Tooltip title="Remove access">
                    <IconButton
                      edge="end"
                      onClick={() =>
                        revoke.mutate(a.id, {
                          onError: (err: unknown) => setError(requestMessage(err, 'Could not remove access')),
                        })
                      }
                      disabled={revoke.isPending}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={a.home_user.username}
                  secondary={
                    a.granted_by ? `${a.home_user.email} · added by ${a.granted_by.username}` : a.home_user.email
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
        {switched && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Switch back to {user?.home_user?.tenant_name ?? 'your organisation'} to add colleagues.
          </Alert>
        )}
        <Autocomplete
          options={switched ? [] : candidates}
          getOptionLabel={(u) => (u.email ? `${u.username} (${u.email})` : u.username)}
          value={null}
          onChange={(_e, value) => {
            if (!value) return;
            setError(null);
            grant.mutate(value.id, {
              onError: (err: unknown) => {
                const fields = requestFieldErrors(err);
                setError(fields.user_id || requestMessage(err, 'Could not add access'));
              },
            });
          }}
          renderInput={(params) => <TextField {...params} size="small" label="Add a colleague" />}
          disabled={grant.isPending || switched}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
